<template>
  <div class="rag-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          RAG Collections
        </h1>
        <div class="d-flex flex-row">
          <v-btn
            color="primary"
            class="mank-btn mr-4"
            @click="createCollection"
            elevation="0">
            <Icon name="plus" variant="solid" color="onPrimary"/>
            Create Collection
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            @click="createSource"
            elevation="0">
            <Icon name="plus" variant="solid" color="onPrimary"/>
            Create Source
          </v-btn>
        </div>
      </div>

      <div class="main-content">
        <div
          class="table-area table-area--pad table-area--stack"
          :class="{ 'table-area--empty': !hasCollections }">
          <div class="empty-state text-center" v-if="!hasCollections">
            <div class="empty-state-icon mb-4">
              <div class="icon-circle">
                <v-icon size="40" color="primary">
                  mdi-database-outline
                </v-icon>
              </div>
            </div>
            <div class="empty-state-text mb-4">
              <h3 class="ds-panel-title mb-1">
                No RAG Collection Created
              </h3>
              <p class="text-body-2 text--secondary">
                You can create a RAG collection and than source.
              </p>
            </div>
            <v-btn
              color="primary"
              variant="flat"
              size="small"
              elevation="0"
              @click="createCollection">
              <v-icon start>
                mdi-plus
              </v-icon>
              Create Collection
            </v-btn>
          </div>

          <div v-else class="collections-table">
            <v-data-table
              :items-per-page="selectedItemsPerPage"
              :page="page"
              :headers="headers"
              :items="collections"
              :search="search"
              :loading="loading"
              class="collections-data-table"
              item-value="id"
              no-data-text="No collections found"
              loading-text="Loading collections..."
              items-per-page-text="Collections per page:"
              hide-default-footer
              show-current-page>
              <template v-slot:header.name="{ header }">
                {{ header.text.toUpperCase() }}
              </template>

              <!-- Collection Name Column -->
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center cursor-pointer" @click="viewCollectionDetail(item)">
                  <div class="text-element-2 text-bold">
                    {{ item.name }}
                  </div>
                </div>
              </template>

              <!-- Status Column -->
              <template v-slot:item.status="{ item }">
                <v-chip
                  size="small"
                  variant="outlined"
                  class="status-chip">
                  {{ item.status }}
                </v-chip>
              </template>

              <!-- ID Column with Copy Button -->
              <template v-slot:item.id="{ item }">
                <div class="id-column d-flex align-center">
                  <span class="text-element-2 id-text">{{ item.id }}</span>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    @click="copyCollectionId(item.id)"
                    class="copy-btn ml-2">
                    <v-icon size="12">
                      mdi-content-copy
                    </v-icon>
                    <v-tooltip activator="parent" location="top">
                      Copy ID
                    </v-tooltip>
                  </v-btn>
                </div>
              </template>

              <!-- Date Created Column -->
              <template v-slot:item.created_at="{ item }">
                <div class="text-element-2">
                  {{ formatDate(item.created_at) }}
                </div>
              </template>

              <!-- Last Updated Column -->
              <template v-slot:item.last_updated="{ item }">
                <div class="text-element-2">
                  {{ formatDate(item.last_updated) }}
                </div>
              </template>

              <!-- Doc Count Column -->
              <template v-slot:item.doc_count="{ item }">
                <div class="text-element-2">
                  {{ item.doc_count }}
                </div>
              </template>

              <!-- Actions Column -->
              <template v-slot:item.actions="{ item }">
                <div class="action-buttons d-flex align-center gap-1">
                  <v-btn
                    icon
                    @click="confirmDeleteCollection(item)"
                    class="delete-btn mank-btn-icon mr-4">
                    <Icon name="trash" variant="solid" color="default" size="16"/>
                  </v-btn>
                  <v-btn
                    icon
                    @click="editCollection(item)"
                    class="delete-btn mank-btn-icon mr-4">
                    <Icon name="edit" variant="solid" color="default" size="16"/>
                  </v-btn>
                  <v-btn
                    icon
                    @click="addToCollection(item)"
                    class="delete-btn mank-btn-icon active">
                    <Icon name="plus" variant="solid" color="onPrimary" size="16"/>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <div class="table-footer">
              <v-pagination
                v-model="page"
                :total-visible="6"
                :length="totalPages"/>
              <div class="pagination-info ml-auto mr-0">
                <span class="text-caption-1 text--secondary">
                  Showing
                </span>
                <v-select
                  class="items-per-page-select"
                  :items="itemsPerPage"
                  v-model="selectedItemsPerPage"
                  hide-details
                  outlined>
                  <template v-slot:append>
                    <Icon name="expand-down" variant="solid" color="muted" size="16"/>
                  </template>
                </v-select>
                <span class="text-caption-1 text--secondary">
                  / {{ totalItems }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>

    <!-- Create Collection Drawer -->
    <CreateCollectionDrawer
      :visible="showCreateDrawer"
      :collection="collectionToEdit"
      @close="closeCreateDrawer"
      @created="onCollectionCreated"
      @updated="onCollectionUpdated"/>
  </div>
</template>

<script>
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.vue';
import CreateCollectionDrawer from '../components/CreateCollectionDrawer.vue';
import CollectionService from '../services/CollectionService';

export default {
  name: 'RAG',
  components: {
    DeleteConfirmationModal,
    CreateCollectionDrawer,
  },
  data() {
    return {
      selectedItemsPerPage: 5,
      itemsPerPage: [5, 10, 20, 50, 100],
      search: '',
      loading: false,
      page: 1,
      showDeleteDialog: false,
      collectionToDelete: null,
      showCreateDrawer: false,
      collectionToEdit: null,

      headers: [
        {
          text: 'Collection Name',
          value: 'name',
          sortable: false,
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
        },
        {
          text: 'ID',
          value: 'id',
          sortable: false,
        },
        {
          text: 'Date Created',
          value: 'created_at',
          sortable: true,
        },
        {
          text: 'Last Updated',
          value: 'last_updated',
          sortable: true,
        },
        {
          text: 'Doc Count',
          value: 'doc_count',
          sortable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
        },
      ],
      collections: [],
    };
  },
  computed: {
    hasCollections() {
      return this.collections && this.collections.length > 0;
    },
    totalItems() {
      return this.collections.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.selectedItemsPerPage);
    },
  },
  async mounted() {
    await this.fetchCollections();
  },
  methods: {
    async fetchCollections() {
      this.loading = true;
      try {
        const response = await CollectionService.getAllCollections();
        this.collections = response.data || response || [];

        // Transform data to match the expected format
        this.collections = this.collections.map((collection) => ({
          ...collection,
          status: this.determineStatus(collection),
          doc_count: collection.doc_count || 0,
          last_updated: collection.updated_at || collection.created_at,
        }));
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to load collections',
        });
        this.collections = [];
      } finally {
        this.loading = false;
      }
    },

    determineStatus(collection) {
      // Determine status based on collection properties
      if (collection.doc_count > 0) {
        return 'Ready';
      }
      if (collection.status) {
        return collection.status;
      }
      return 'Empty';
    },
    createCollection() {
      this.showCreateDrawer = true;
    },
    closeCreateDrawer() {
      this.showCreateDrawer = false;
      this.collectionToEdit = null;
    },
    async onCollectionCreated(newCollection) {
      this.collections.unshift({
        ...newCollection,
        status: 'Empty',
        doc_count: 0,
        last_updated: newCollection.updated_at || newCollection.created_at,
      });
    },

    async onCollectionUpdated(updatedCollection) {
      const index = this.collections.findIndex((c) => c.id === updatedCollection.id);
      if (index !== -1) {
        this.collections[index] = {
          ...updatedCollection,
          status: this.determineStatus(updatedCollection),
          doc_count: updatedCollection.doc_count || 0,
          last_updated: updatedCollection.updated_at || updatedCollection.created_at,
        };
      }
    },
    viewCollectionDetail(collection) {
      this.$router.push({
        name: 'CollectionDetail',
        params: { id: collection.id },
      });
    },
    editCollection(collection) {
      this.collectionToEdit = { ...collection };
      this.showCreateDrawer = true;
    },
    confirmDeleteCollection(collection) {
      this.collectionToDelete = collection;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.collectionToDelete = null;
    },
    async confirmDelete() {
      if (this.collectionToDelete) {
        try {
          await CollectionService.deleteCollection(this.collectionToDelete.id);

          // Remove from local list
          const index = this.collections.findIndex((c) => c.id === this.collectionToDelete.id);
          if (index !== -1) {
            this.collections.splice(index, 1);
          }

          // Show success notification
          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: 'Collection deleted successfully',
          });
        } catch (error) {
          // Show error notification
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: 'Failed to delete collection',
          });
        }
      }
      this.showDeleteDialog = false;
      this.collectionToDelete = null;
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    getStatusColor(status) {
      switch (status) {
      case 'Ready':
        return 'success';
      case 'Processing':
        return 'warning';
      case 'Empty':
        return 'grey';
      case 'Error':
        return 'error';
      default:
        return 'grey';
      }
    },
    async copyCollectionId(id) {
      try {
        await navigator.clipboard.writeText(id);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Collection ID copied to clipboard',
        });
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = id;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Collection ID copied to clipboard',
        });
      }
    },
    addToCollection(collection) {
      this.$router.push({
        name: 'CollectionDetail',
        params: { id: collection.id },
      });
    },
    createSource() {
      this.$router.push({ name: 'CreateSource' });
    },

  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 400px;
}

.empty-state-icon {
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: $light-surface-1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.empty-state-text {
  text-align: center;

  h3 {
    color: $light-foreground-1;
    margin-bottom: 4px;
  }

  p {
    color: $light-foreground-2;
    line-height: 1.6;
  }
}

.collections-table {
  width: 100%;
  background-color: $light-surface-1;
  border-radius: $card-border-radius;
  overflow: hidden;
}

.action-buttons {
  justify-content: flex-end;
}

.action-btn {
  &:hover {
    background-color: $light-surface-2;
  }
}

.add-btn {
  &:hover {
    background-color: $light-primary;
  }
}

.status-chip {
  font-weight: 600;
  min-width: 48px;
  background: transparent !important;
  border: 1px solid $light-border !important;
  color: $light-foreground-1 !important;
  width: 96px;
  text-align: center;
  justify-content: center;
}

.id-column {
  max-width: 200px;

  .id-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy-btn {
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
      background-color: $light-surface-2;
    }
  }
}

.table-footer {
  background: $light-surface-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  border-top: 1px solid $light-border;
  height: 48px;
}

.pagination-info {
  font-size: $caption-1-size;
  color: $light-foreground-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.items-per-page-select {
  width: 48px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
}

// Cursor styles
.cursor-pointer {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

// Pagination styles (copied from Agents.vue)
::v-deep .v-pagination .v-pagination__navigation {
  box-shadow: none;
  border: none;
}

::v-deep .v-pagination .v-pagination__item {
  box-shadow: none;
  border: none;
  font-weight: 600;
  width: 24px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  min-width: 24px;
}

::v-deep .v-pagination .v-pagination__item--active {
  background-color: transparent !important;
  color: $light-primary;
  border: 1px solid $light-primary-neutral;
}

::v-deep .v-data-table-header {
  background-color: $light-surface-1;
}

::v-deep .v-data-table-header tr th {
  color: $light-foreground-3 !important;
  font-weight: 600;
  font-size: $body-3-size !important;
  text-transform: capitalize;
}

// Responsive design
@media (max-width: map-get($grid-breakpoints, md)) {
  .page-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .empty-state {
    padding: 20px;
  }
}
</style>
